/**
 * Blow Room Maintenance Form — Layer 2.
 *
 * Checklist-driven. Operator ticks the OK boxes; flips a switch + fills
 * a few fields if a breakdown happened. The form posts to the
 * /checklist-logs endpoint with entry_type=BR_MAINT, and (when the
 * breakdown switch is on) the same call creates a MachineBreakdown
 * row that the production-log efficiency hook will auto-pick up.
 */
import { useState } from 'react'
import {
  App, Button, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber,
  Row, Select, Space, Switch, Typography,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { filterActiveShifts } from '@/shared/constants/shifts'
import { plantsApi } from '@/features/masters/plants/api'
import { machinesApi } from '@/features/masters/machines/api'
import { shiftsApi } from '@/features/gate/api'
import { checklistLogsApi } from '@/features/baleManagement/api'

const { Text } = Typography

// Order matters — this is the visual layout of the checkbox grid.
const BR_CHECKLIST: Array<{ key: string; label: string }> = [
  { key: 'safety_guards_ok',          label: 'Safety guards OK' },
  { key: 'emergency_stop_ok',         label: 'Emergency stop OK' },
  { key: 'suction_ok',                label: 'Suction OK' },
  { key: 'duct_choking',              label: 'Duct choking (issue)' },
  { key: 'filter_cleaned',            label: 'Filter cleaned' },
  { key: 'belt_condition_ok',         label: 'Belt condition OK' },
  { key: 'bearing_sound_normal',      label: 'Bearing sound normal' },
  { key: 'beater_condition_ok',       label: 'Beater condition OK' },
  { key: 'grid_bar_clean',            label: 'Grid bar area clean' },
  { key: 'contamination_clearer_ok',  label: 'Contamination clearer OK' },
  { key: 'rotary_valves_ok',          label: 'Rotary valves OK' },
  { key: 'electrical_panel_normal',   label: 'Electrical panel normal' },
  { key: 'fire_risk_checked',         label: 'Fire-risk points checked' },
  { key: 'lubrication_done',          label: 'Lubrication done' },
  { key: 'abnormal_vibration',        label: 'Abnormal vibration (issue)' },
  { key: 'abnormal_heating',          label: 'Abnormal heating (issue)' },
]

const BREAKDOWN_CATEGORIES = [
  { value: 'M', label: 'M — Mechanical' },
  { value: 'E', label: 'E — Electrical' },
  { value: 'Q', label: 'Q — Quality Hold' },
  { value: 'P', label: 'P — Material Pending' },
  { value: 'U', label: 'U — Utility' },
  { value: 'C', label: 'C — Cleaning' },
  { value: 'S', label: 'S — Setup' },
]

export default function BlowRoomMaintenancePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [breakdownYes, setBreakdownYes] = useState(false)

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []

  const { data: machinesData } = useQuery({
    queryKey: ['machines-blowroom-maint', plantId],
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 200 }),
    enabled: !!plantId,
  })
  const brMachines = (machinesData?.items || []).filter((m: any) => {
    const t = (m.section || '').toUpperCase()
    return /BLOW.?ROOM|BLENDOMAT|BALE.?OPENER|OPENING.?LINE/.test(t)
  })

  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-all-active'],
    queryFn: async () => filterActiveShifts(await shiftsApi.list()),
  })

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => checklistLogsApi.upsert(body),
    onSuccess: (res: any) => {
      message.success(
        res.created
          ? 'Maintenance log saved'
          : 'Maintenance log updated for this shift'
      )
      qc.invalidateQueries({ queryKey: ['br-maint'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  const onSubmit = async () => {
    const v = await form.validateFields()
    if (!plantId || !shiftId) {
      message.error('Plant + shift are required')
      return
    }
    const items: Record<string, any> = {}
    BR_CHECKLIST.forEach(({ key }) => { items[key] = !!v[key] })
    items.breakdown_yes = !!breakdownYes

    const breakdown = breakdownYes ? {
      breakdown_time: v.breakdown_time?.toISOString(),
      restoration_time: v.restoration_time?.toISOString(),
      duration_minutes: v.duration_minutes ?? null,
      category: v.breakdown_category || 'M',
      description: v.breakdown_reason,
      action_taken: v.action_taken,
      spare_used: v.spare_used,
    } : undefined

    saveMutation.mutate({
      plant_id: plantId,
      log_date: dayjs().format('YYYY-MM-DD'),
      entry_type: 'BR_MAINT',
      shift_id: shiftId,
      machine_id: machineId || null,
      blow_room_line_id: null,
      items_json: items,
      remarks: v.remarks || null,
      breakdown,
    })
  }

  return (
    <div>
      <PageHeader
        title="Blow Room — Maintenance"
        subtitle="Tick the OK boxes; flip the breakdown switch only if something failed."
      />
      <Card>
        <Form layout="vertical" form={form} size="large">
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Plant" required>
                <Select options={plantOptions} value={plantId} onChange={setPlantId} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="BR Line">
                <Select
                  options={brMachines.map((m: any) => ({
                    label: `${m.machine_id_str} — ${m.name}`, value: m.id,
                  }))}
                  value={machineId} onChange={setMachineId}
                  placeholder="Select line (optional)"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Shift" required>
                <Select
                  options={(shifts || []).map((s: any) => ({
                    label: s.shift_name || s.shift_code, value: s.id,
                  }))}
                  value={shiftId} onChange={setShiftId}
                />
              </Form.Item>
            </Col>
          </Row>

          <Card type="inner" title="Daily checklist — tick what's OK / done">
            <Row gutter={[12, 8]}>
              {BR_CHECKLIST.map(({ key, label }) => (
                <Col xs={24} sm={12} md={8} key={key}>
                  <Form.Item name={key} valuePropName="checked" style={{ marginBottom: 4 }}>
                    <Checkbox>{label}</Checkbox>
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>

          <Card
            type="inner" title={
              <Space>
                <span>Breakdown</span>
                <Switch
                  checked={breakdownYes}
                  onChange={setBreakdownYes}
                  checkedChildren="YES" unCheckedChildren="NO"
                />
              </Space>
            }
            style={{ marginTop: 12 }}
          >
            {!breakdownYes ? (
              <Text type="secondary">No breakdown reported. Skip this section.</Text>
            ) : (
              <>
                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="breakdown_time" label="Start time">
                      <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="restoration_time" label="End time">
                      <DatePicker showTime style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="duration_minutes" label="Duration (min) — optional">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="breakdown_category" label="Category" initialValue="M">
                      <Select options={BREAKDOWN_CATEGORIES} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item name="breakdown_reason" label="Reason / description">
                      <Input placeholder="e.g. Beater bearing seized" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="action_taken" label="Action taken">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="spare_used" label="Spare used (if any)">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Card>

          <Form.Item name="remarks" label="Remarks" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} maxLength={300} />
          </Form.Item>

          <Button
            type="primary" size="large" block icon={<SaveOutlined />}
            loading={saveMutation.isPending} onClick={onSubmit}
          >
            Save Maintenance Log
          </Button>
        </Form>
      </Card>
    </div>
  )
}
